import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import { Link, useLocation } from "react-router-dom";
import { MenuListType, SidebarItemTypes } from "../../Types/Layout/SidebarType";
import { handlePined, setToggleSidebar } from "../../ReduxToolkit/Reducers/LayoutSlice";
import { LI, SVG, UL } from "../../AbstractElements";
import { Href } from "../../utils/Constant";
import { useTranslation } from "react-i18next";
import {
  isTeacherLeaderDisabled,
  isTeacherLeaderTabAllowed,
  TEACHER_LEADER_MAIN_MENU_TITLES,
  TEACHER_LEADER_ALLOWED_SIDEBAR_CHILDREN,
  TEACHER_LEADER_BLOCKED_SIDEBAR_CHILDREN,
  PROFILE_DATA_UPDATED_EVENT,
} from "../../utils/permissions";
import { fetchTasksMe, isTasksLeaderRole, RM_TASKS_ENABLED } from "../../Tasks/tasksApi";
import { currentSalesRole } from "../../Reports/reportsApi";

/**
 * Nested groups (level 1+, e.g. Consumer Xperience → RM) remember being
 * expanded or collapsed, per browser, independently of each other. Until a
 * group is clicked it follows the route as before. Top-level sections keep
 * their one-open-at-a-time accordion.
 */
const OPEN_GROUPS_KEY = "sidebarOpenGroups";
const readOpenGroups = (): Record<string, boolean> => {
  try {
    const saved = JSON.parse(localStorage.getItem(OPEN_GROUPS_KEY) || "{}");
    return saved && typeof saved === "object" ? saved : {};
  } catch {
    return {};
  }
};

const Menulist: React.FC<MenuListType> = ({ menu, setActiveMenu, activeMenu, level, className }) => {
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(readOpenGroups);
  /** Whether a group's children show: its remembered state (nested groups), else the accordion. */
  const isOpen = (title?: string): boolean =>
    level >= 1 && title && title in openGroups ? openGroups[title] : activeMenu[level] === title;
  const toggleGroup = (title: string) => {
    const next = { ...readOpenGroups(), [title]: !isOpen(title) };
    setOpenGroups(next);
    try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next)); } catch { /* remembering is a nicety */ }
  };
  const { pinedMenu } = useAppSelector((state) => state.layout);
  const { sidebarIconType } = useAppSelector((state) => state.themeCustomizer);
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [tlDisabled, setTlDisabled] = useState(isTeacherLeaderDisabled());
  // RM Leaves is a leader's screen: the service refuses the writes from an
  // agent token and the page itself says no, so showing the entry to every
  // RM would only offer a door that doesn't open. Undefined while the role
  // is still loading — hide it until we know, rather than flash it and
  // withdraw it.
  const [rmLeader, setRmLeader] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (!RM_TASKS_ENABLED) return;
    let cancelled = false;
    fetchTasksMe()
      .then((me) => { if (!cancelled) setRmLeader(isTasksLeaderRole(me?.role)); })
      .catch(() => { if (!cancelled) setRmLeader(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const refresh = () => setTlDisabled(isTeacherLeaderDisabled());
    refresh();
    window.addEventListener(PROFILE_DATA_UPDATED_EVENT, refresh);
    return () => window.removeEventListener(PROFILE_DATA_UPDATED_EVENT, refresh);
  }, []);

  const ActiveNavLinkUrl = (path?: string, active?: boolean) => {
    return location.pathname === path ? (active ? active : true) : "";
  };

  const shouldSetActive = ({ item }: SidebarItemTypes) => {
    let returnValue = false;
    if (item?.path === location.pathname) returnValue = true;
    if (!returnValue && item?.children) {
      item?.children.every((subItem) => {
        returnValue = shouldSetActive({ item: subItem });
        return !returnValue;
      });
    }
    return returnValue;
  };

  const isItemDisabled = (itemTitle?: string) => {
    if (!tlDisabled) return false;
    if (level === 0) {
      return !isTeacherLeaderTabAllowed(itemTitle, TEACHER_LEADER_MAIN_MENU_TITLES);
    }
    if (isTeacherLeaderTabAllowed(itemTitle, TEACHER_LEADER_BLOCKED_SIDEBAR_CHILDREN)) return true;
    return !isTeacherLeaderTabAllowed(itemTitle, TEACHER_LEADER_ALLOWED_SIDEBAR_CHILDREN);
  };

  const handleClick = (item: string, disabled: boolean) => {
    if (disabled) return;
    const temp = activeMenu;
    temp[level] = item !== temp[level] ? item : "";
    setActiveMenu([...temp]);
  };

  useEffect(() => {
    menu?.forEach((item: any) => {
      const gotValue = shouldSetActive({ item });
      if (gotValue) {
        const temp = [...activeMenu];
        temp[level] = item.title;
        setActiveMenu(temp);
      }
    });
  }, []);

  /**
   * Menu entries only a team lead or admin should see.
   *
   * Each of these refuses an agent's token at the page itself, so listing
   * them for everyone only offers a door that does not open. Dashboard and
   * Pods (Consumer Xperience → RM) are deliberately absent: both work for
   * any RM — the dashboard is their own queue, with extra sections for a leader.
   */
  const LEADER_ONLY = ['Leaves', 'Relationship Managers', 'Team Performance', 'Task Settings'];
  // Sales screens follow artium-reports' rules: admin screens for SALES_ADMIN
  // and the plain ROLE_ADMIN, Performance for any sales role. It refuses the rest anyway.
  const SALES_ADMIN_ONLY = ['Sales Agents', 'Targets & Incentives'];
  const SALES_ANY_ROLE = ['Performance', 'My Performance'];
  const salesRole = currentSalesRole();
  const salesAdmin = salesRole === 'SALES_ADMIN';
  const visible = (menu ?? []).filter(
    (item) =>
      (!LEADER_ONLY.includes(item.title || '') || rmLeader === true) &&
      (!SALES_ADMIN_ONLY.includes(item.title || '') || salesAdmin) &&
      (!SALES_ANY_ROLE.includes(item.title || '') || salesRole !== null),
  );

  return (
    <>
      {visible.map((item, index) => {
        const disabled = isItemDisabled(item.title);
        return (
          <LI
            key={index}
            className={`${level === 0 ? "sidebar-list" : ""} ${pinedMenu.includes(item.title || "") ? "pined" : ""}  
          ${(item.children
              ? item.children.map((innerItem) => ActiveNavLinkUrl(innerItem.path)).includes(true)
              : ActiveNavLinkUrl(item.path)) || activeMenu[level] === item.title
              ? "active"
              : ""
          } ${disabled ? "tl-menu-restricted" : ""}`}
            title={disabled ? "Disabled for Teacher Leaders" : undefined}
            aria-disabled={disabled}
          >
            {level === 0 && (
              <i className="fa fa-thumb-tack" onClick={() => dispatch(handlePined(item.title))}></i>
            )}
            <Link
              className={`${!className && level !== 2 ? "sidebar-link sidebar-title" : ""} 
            ${(item.children
                ? item.children.map((innerItem) => ActiveNavLinkUrl(innerItem.path)).includes(true)
                : ActiveNavLinkUrl(item.path)) || activeMenu[level] === item.title ? "active" : ""
            }`}
              to={disabled ? Href : item.path ? item.path : Href}
              onClick={(e) => {
                if (disabled) {
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                if (level >= 1 && item.children) toggleGroup(item.title || "");
                else handleClick(item.title || "", disabled);
                // Leaf links: close off-canvas sidebar on mobile after navigate
                if (item.path && !item.children && window.innerWidth < 992) {
                  dispatch(setToggleSidebar(true));
                }
              }}
              style={
                disabled
                  ? { opacity: 0.45, cursor: "not-allowed", pointerEvents: "none" }
                  : undefined
              }
            >
              {item.icon &&
                (item.icon.startsWith("fa-") ? (
                  // Font Awesome menu icons; the template's own entries keep
                  // using the SVG sprite via the branch below.
                  <i className={`fa ${item.icon} sidebar-fa-icon`} />
                ) : (
                  <SVG className={`${sidebarIconType}-icon`} iconId={`${sidebarIconType}-${item.icon}`} />
                ))}
              <span className={item.lanClass && item.lanClass}>{item.title as string}</span>
              {item.children &&
                (isOpen(item.title) ? (
                  <div className="according-menu">
                    <i className="fa fa-angle-down" />
                  </div>
                ) : (
                  <div className="according-menu">
                    <i className="fa fa-angle-right" />
                  </div>
                ))}
            </Link>
            {item.children && !disabled && (
              <UL
                className={`simple-list ${
                  level !== 0 ? "nav-sub-childmenu submenu-content" : "sidebar-submenu "
                }`}
                style={{
                  // Open/closed is driven by clicks alone (the mount effect
                  // seeds activeMenu from the route). Including the route
                  // match here made the current page's menu impossible to
                  // collapse — its display stayed "block" whatever you did.
                  display: `${isOpen(item.title) ? "block" : "none"}`,
                }}
              >
                <Menulist
                  menu={item.children}
                  activeMenu={activeMenu}
                  setActiveMenu={setActiveMenu}
                  level={level + 1}
                  className="sidebar-submenu"
                />
              </UL>
            )}
          </LI>
        );
      })}
    </>
  );
};

export default Menulist;
